import axios, { CreateAxiosDefaults } from "axios";
import { getApiUrl, handleImpersonation, handleProfiling } from "./utils";
import i18next from "i18next";
import { withQuestionnairePostOrganization } from "./questionnairePostScope";
import { withViewedCampaign } from "./campaignScope";

const userConfig: CreateAxiosDefaults = {
    baseURL: getApiUrl(),
    withCredentials: true,
    headers: {
        common: {
            "Content-Type": "application/ld+json",
            Accept: "application/ld+json",
        },
    },
};

export const publicApiCall = axios.create({ ...userConfig });

const getExistingLocaleHeader = (headers: unknown): string | undefined => {
    if (!headers) {
        return undefined;
    }

    if (typeof headers === "object" && headers !== null && "get" in headers && typeof headers.get === "function") {
        const value = headers.get("Accept-Language");
        return typeof value === "string" ? value : undefined;
    }

    if (typeof headers !== "object" || headers === null) {
        return undefined;
    }

    const typedHeaders = headers as Record<string, unknown>;
    const directValue = typedHeaders["Accept-Language"] ?? typedHeaders["accept-language"];

    return typeof directValue === "string" ? directValue : undefined;
};

export const resolveRequestLocale = (
    configuredLocale: string | null | undefined,
    currentLocale: string | null | undefined,
    fallbackLocale = "en",
) => {
    const normalizedConfiguredLocale = configuredLocale?.trim();

    if (normalizedConfiguredLocale && normalizedConfiguredLocale.length > 0) {
        return normalizedConfiguredLocale;
    }

    const normalizedCurrentLocale = currentLocale?.trim();

    return normalizedCurrentLocale && normalizedCurrentLocale.length > 0 ? normalizedCurrentLocale : fallbackLocale;
};

const applyLocaleHeader = <T extends { headers?: unknown }>(config: T) => {
    const resolvedLocale = resolveRequestLocale(getExistingLocaleHeader(config.headers), i18next.resolvedLanguage);

    if (
        typeof config.headers === "object" &&
        config.headers !== null &&
        "set" in config.headers &&
        typeof config.headers.set === "function"
    ) {
        config.headers.set("Accept-Language", resolvedLocale);
        return config;
    }

    config.headers = {
        ...(typeof config.headers === "object" && config.headers !== null ?
            (config.headers as Record<string, unknown>)
        :   {}),
        "Accept-Language": resolvedLocale,
    };

    return config;
};

export const loggingOutSession = axios.create({ ...userConfig, withCredentials: true });
loggingOutSession.interceptors.request.use((config) => {
    const _config = handleImpersonation(config);
    return _config;
});

export const loggedInApiCall = axios.create({ ...userConfig, withCredentials: true });
loggedInApiCall.interceptors.request.use((config) => {
    handleImpersonation(config);
    handleProfiling(config);
    withQuestionnairePostOrganization(config);
    withViewedCampaign(config);

    return applyLocaleHeader(config);
});

const requestQueue: Array<() => void> = [];
let isProcessing = false;

const processQueue = () => {
    if (requestQueue.length > 0 && !isProcessing) {
        isProcessing = true;
        const next = requestQueue.shift();
        next?.();
    }
};

export const loggedInApiSingleConcurrentCall = axios.create({ ...userConfig, withCredentials: true });
loggedInApiSingleConcurrentCall.interceptors.request.use((config) => {
    handleImpersonation(config);
    handleProfiling(config);
    withQuestionnairePostOrganization(config);
    withViewedCampaign(config);

    return new Promise((resolve) => {
        const execute = () => resolve(applyLocaleHeader(config));

        if (!isProcessing) {
            isProcessing = true;
            execute();
        } else {
            requestQueue.push(execute);
        }
    });
});

loggedInApiSingleConcurrentCall.interceptors.response.use(
    function (response) {
        isProcessing = false;
        processQueue();
        return response;
    },
    function (error) {
        isProcessing = false;
        processQueue();
        return Promise.reject(error);
    },
);
